import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import chatSvgPaths from '../imports/svg-gb9dxauhw0';
import { Participant } from '../App';
import { MetadataViewer } from './MetadataViewer';
import { hasRichMetadata } from '../utils/metadataUtils';
import { ConversationAnalysisPanel } from './analytics/ConversationAnalysisPanel';
import { shouldRenderMessageAsImages } from '../utils/messageImages';
import { extractReactionSummary } from '../utils/messageReactions';
import type { ReactionKind } from '../utils/messageReactions';
import { ReactionMenu } from './ReactionMenu';
import { ConversationInteractionTag } from './ConversationInteractionTag';
import { Carousel_002 } from './Carousel_002';

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isOwn: boolean;
  createdAt: Date;
  metadata?: Record<string, any>;
  messageType?: string;
  interactionType?: string;
  feedItemId?: string;
  postId?: string;
  replyId?: string;
  deliveryStatus?: 'sending' | 'sent' | 'failed';
  deliveryError?: string;
}

interface ExpandedConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversation: {
    id: string;
    name: string;
    phoneNumber: string;
    userPhoneNumber: string;
    profileImage?: string;
    username?: string;
    messages: Message[];
    isGroup: boolean;
    lastMessageTime: Date;
    participants?: Participant[];
  } | null;
  onSendMessage: (conversationId: string, message: string) => void;
  onReactMessage?: (conversationId: string, messageId: string, reaction: ReactionKind) => Promise<void>;
  onLiveMessage?: (conversationId: string, messageData: any) => void;
}

function MessageBubble({
  message,
  onOpenMetadata,
  onOpenReactionMenu,
}: {
  message: Message;
  onOpenMetadata?: (data: Record<string, any>) => void;
  onOpenReactionMenu?: (message: Message, event: React.MouseEvent) => void;
}) {
  const { isImage, imageUrls } = shouldRenderMessageAsImages({
    text: message.text,
    messageType: message.messageType,
  });
  const reactionChips = extractReactionSummary(message.metadata);

  const getMessageA11yLabel = () => {
    const direction = message.isOwn ? 'sent' : 'received';
    if (isImage) return `Image message (${direction}). Press Enter to react.`;
    const snippet = String(message.text || '').trim().slice(0, 140);
    return `Message (${direction}): ${snippet || 'empty'}. Press Enter to react.`;
  };

  const getReactionButtonLabel = () => {
    if (isImage) return 'React to image message';
    const snippet = String(message.text || '').trim().slice(0, 80);
    return `React to message: ${snippet || 'empty'}`;
  };

  const handleReactionKeyDown = (event: React.KeyboardEvent) => {
    if (!onOpenReactionMenu) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget as HTMLElement;
    const rect = element.getBoundingClientRect();
    onOpenReactionMenu(
      message,
      {
        preventDefault: () => {},
        stopPropagation: () => {},
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      } as unknown as React.MouseEvent
    );
  };

  if (isImage) {
    return (
      <div className={`flex flex-col ${reactionChips.length > 0 ? 'mb-6' : 'mb-4'} ${message.isOwn ? 'items-end' : 'items-start'}`}>
        <div className={`max-w-[75%] relative ${message.isOwn ? 'mr-1' : 'ml-1'}`}>
          <div
            className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-white/70 dark:bg-gray-900/40 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            onContextMenu={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
            onKeyDown={handleReactionKeyDown}
            tabIndex={0}
            aria-label={getMessageA11yLabel()}
          >
            {/* Screen-reader / automation hook to open reaction menu */}
            <button
              type="button"
              className="sr-only"
              onClick={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
              aria-label={getReactionButtonLabel()}
            >
              React
            </button>
            <Carousel_002
              images={imageUrls.map((url, index) => ({
                src: url,
                alt: `Message image ${index + 1}`,
              }))}
              className="w-full"
              showNavigation
              showPagination
              loop
            />

            {message.metadata && hasRichMetadata(message.metadata) && (
              <div className="px-3 py-2 border-t border-gray-200/70 dark:border-gray-700/60">
                <button
                  type="button"
                  onClick={() => onOpenMetadata && onOpenMetadata(message.metadata as Record<string, any>)}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border text-gray-700 dark:text-gray-200 border-gray-300/70 dark:border-gray-700 hover:bg-gray-100/70 dark:hover:bg-gray-800/50 transition-colors"
                  title="View tool call details"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                    <path fillRule="evenodd" d="M12 2.25a9.75 9.75 0 1 0 0 19.5 9.75 9.75 0 0 0 0-19.5Zm.75 6a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0V8.25Zm0 7.5a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z" clipRule="evenodd" />
                  </svg>
                  Tool call
                </button>
              </div>
            )}
          </div>

          {/* iMessage-style reaction overlay */}
          {reactionChips.length > 0 && (
            <div
              className={`pointer-events-none absolute ${message.isOwn ? 'right-2 -bottom-3' : 'left-2 -bottom-3'}`}
              aria-hidden="true"
            >
              <div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200/70 dark:border-gray-700/60 bg-white/95 dark:bg-gray-950/90 shadow px-2 py-1 text-[13px] leading-none">
                {reactionChips.slice(0, 3).map((chip, idx) => (
                  <span key={`${message.id}-reaction-emoji-${idx}`}>{chip.emoji}</span>
                ))}
                {reactionChips.length > 3 && (
                  <span className="ml-1 text-[11px] text-gray-600 dark:text-gray-200">+{reactionChips.length - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Timestamp - Outside and underneath */}
        <div className={`${reactionChips.length > 0 ? 'mt-4' : 'mt-1'} px-1 ${message.isOwn ? 'mr-3' : 'ml-3'}`}>
          <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-[11px] text-[#8E8E93] dark:text-gray-500 tracking-[-0.1px]">
            {message.timestamp}
            {message.isOwn && message.deliveryStatus === 'sending' && (
              <span className="ml-2 text-gray-500 dark:text-gray-400">Sending…</span>
            )}
            {message.isOwn && message.deliveryStatus === 'failed' && (
              <span
                className="ml-2 text-red-600 dark:text-red-400 font-semibold"
                title={message.deliveryError || 'Not delivered'}
              >
                Not delivered
              </span>
            )}
          </p>
          <ConversationInteractionTag
            interactionType={message.interactionType}
            feedItemId={message.feedItemId}
            postId={message.postId}
            replyId={message.replyId}
            metadata={message.metadata}
            className="mt-1"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${reactionChips.length > 0 ? 'mb-6' : 'mb-4'} ${message.isOwn ? 'items-end' : 'items-start'}`}>
      {/* Message Bubble */}
      <div className={`max-w-[75%] relative ${message.isOwn ? 'mr-1' : 'ml-1'}`}>
        <div className={`
          relative px-4 py-2.5 shadow-sm
          ${message.isOwn 
            ? 'bg-[#007AFF] text-white' 
            : 'bg-[#E5E5EA] dark:bg-gray-700 text-black dark:text-gray-100'
          }
          focus:outline-none focus:ring-2 focus:ring-blue-500/40
        `}
        style={{
          borderRadius: message.isOwn 
            ? '18px 18px 4px 18px' 
            : '18px 18px 18px 4px'
        }}
        onContextMenu={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
        onKeyDown={handleReactionKeyDown}
        tabIndex={0}
        aria-label={getMessageA11yLabel()}
        >
          {/* Screen-reader / automation hook to open reaction menu */}
          <button
            type="button"
            className="sr-only"
            onClick={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
            aria-label={getReactionButtonLabel()}
          >
            React
          </button>
          <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-[17px] leading-[22px] tracking-[-0.2px]">
            {message.text}
          </p>
          {message.metadata && hasRichMetadata(message.metadata) && (
            <button
              type="button"
              onClick={() => onOpenMetadata && onOpenMetadata(message.metadata as Record<string, any>)}
              className={`mt-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${message.isOwn ? 'text-white/90 border-white/30 hover:bg-white/10' : 'text-gray-700 border-gray-400/50 hover:bg-gray-200/70'} transition-colors`}
              title="View tool call details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                <path fillRule="evenodd" d="M12 2.25a9.75 9.75 0 1 0 0 19.5 9.75 9.75 0 0 0 0-19.5Zm.75 6a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0V8.25Zm0 7.5a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z" clipRule="evenodd" />
              </svg>
              Tool call
            </button>
          )}
          
          {/* Message Tail */}
          <div 
            className={`absolute bottom-0 ${message.isOwn ? 'right-[-1px]' : 'left-[-1px]'}`}
          >
            <svg 
              width="12" 
              height="19" 
              viewBox="0 0 12 19" 
              fill="none"
            >
              {message.isOwn ? (
                <path 
                  d="M0 0v19c6 0 12-4 12-9V0H0z" 
                  fill="#007AFF"
                />
              ) : (
                <path 
                  d="M12 0v19c-6 0-12-4-12-9V0h12z" 
                  className="fill-[#E5E5EA] dark:fill-gray-700"
                />
              )}
            </svg>
          </div>
        </div>

        {/* iMessage-style reaction overlay */}
        {reactionChips.length > 0 && (
          <div
            className={`pointer-events-none absolute ${message.isOwn ? 'right-2 -bottom-3' : 'left-2 -bottom-3'}`}
            aria-hidden="true"
          >
            <div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200/70 dark:border-gray-700/60 bg-white/95 dark:bg-gray-950/90 shadow px-2 py-1 text-[13px] leading-none">
              {reactionChips.slice(0, 3).map((chip, idx) => (
                <span key={`${message.id}-reaction-emoji-${idx}`}>{chip.emoji}</span>
              ))}
              {reactionChips.length > 3 && (
                <span className="ml-1 text-[11px] text-gray-600 dark:text-gray-200">+{reactionChips.length - 3}</span>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Timestamp - Outside and underneath the bubble */}
      <div className={`${reactionChips.length > 0 ? 'mt-4' : 'mt-1'} px-1 ${message.isOwn ? 'mr-3' : 'ml-3'}`}>
        <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-[11px] text-[#8E8E93] dark:text-gray-500 tracking-[-0.1px]">
          {message.timestamp}
          {message.isOwn && message.deliveryStatus === 'sending' && (
            <span className="ml-2 text-gray-500 dark:text-gray-400">Sending…</span>
          )}
          {message.isOwn && message.deliveryStatus === 'failed' && (
            <span
              className="ml-2 text-red-600 dark:text-red-400 font-semibold"
              title={message.deliveryError || 'Not delivered'}
            >
              Not delivered
            </span>
          )}
        </p>
        <ConversationInteractionTag
          interactionType={message.interactionType}
          feedItemId={message.feedItemId}
          postId={message.postId}
          replyId={message.replyId}
          metadata={message.metadata}
          className="mt-1"
        />
      </div>
    </div>
  );
}

function MessageInput({ 
  onSendMessage, 
  disabled = false 
}: { 
  onSendMessage: (message: string) => void; 
  disabled?: boolean;
}) {
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
      <div className="relative flex items-center">
        <div className="flex-1 relative">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={disabled ? "Disabled" : "iMessage"}
            disabled={disabled}
            className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 font-['SF_Pro:Regular',_sans-serif] text-base pr-12 shadow-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          />
          <button
            type="submit"
            disabled={disabled}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center ${
              disabled ? 'opacity-30 cursor-not-allowed' : ''
            }`}
          >
            <svg className="w-5 h-5" fill="none" preserveAspectRatio="none" viewBox="0 0 27 27">
              <path d={chatSvgPaths.p20df2880} fill="black" opacity="0.45" />
            </svg>
          </button>
        </div>
      </div>
    </form>
  );
}

export function ExpandedConversationModal({
  isOpen,
  onClose,
  conversation,
  onSendMessage,
  onReactMessage,
  onLiveMessage
}: ExpandedConversationModalProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isLiveConnected, setIsLiveConnected] = useState(false);
  const [isMetadataOpen, setIsMetadataOpen] = useState(false);
  const [activeMetadata, setActiveMetadata] = useState<Record<string, any> | null>(null);
  const [activeTab, setActiveTab] = useState<'messages' | 'analysis'>('messages');
  const pinnedToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const lastScrollDirectionRef = useRef<'up' | 'down' | null>(null);
  const lastScrollAtRef = useRef(0);

  const [reactionMenu, setReactionMenu] = useState<{ messageId: string; x: number; y: number } | null>(null);
  const [reactionSending, setReactionSending] = useState(false);
  const [reactionError, setReactionError] = useState<string | null>(null);
  const reactionMenuRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = (forceImmediate = false) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: forceImmediate ? 'auto' : 'smooth'
      });
    }
  };

  // Check if user is at bottom of message container
  const isAtBottom = useCallback(() => {
    if (!messagesContainerRef.current) return true;
    const container = messagesContainerRef.current;
    const threshold = 50;
    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
  }, []);

  // Handle scroll events
  const handleMessageScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const nextTop = container.scrollTop;
    const prevTop = lastScrollTopRef.current;
    if (nextTop < prevTop) {
      lastScrollDirectionRef.current = 'up';
      lastScrollAtRef.current = Date.now();
    } else if (nextTop > prevTop) {
      lastScrollDirectionRef.current = 'down';
      lastScrollAtRef.current = Date.now();
    }
    lastScrollTopRef.current = nextTop;

    const atBottom = isAtBottom();
    pinnedToBottomRef.current = atBottom;
    setShowScrollToBottom(!atBottom);
  }, [isAtBottom]);

  // Auto-scroll when messages change and user is at bottom
  useEffect(() => {
    if (!isOpen) return;

    const recentlyScrolledUp =
      lastScrollDirectionRef.current === 'up' && Date.now() - lastScrollAtRef.current < 600;
    if (recentlyScrolledUp) return;

    if (pinnedToBottomRef.current) {
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [conversation?.messages, isOpen, isAtBottom]);

  // Scroll to bottom when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        scrollToBottom(true);
        pinnedToBottomRef.current = true;
        setShowScrollToBottom(false);
        lastScrollDirectionRef.current = 'down';
        lastScrollAtRef.current = Date.now();
      }, 100);
    }
  }, [isOpen]);

  // Subscribe to SSE for this conversation when modal is open
  useEffect(() => {
    if (!isOpen || !conversation) {
      setIsLiveConnected(false);
      return;
    }

    const conversationId = conversation.id;
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    
    const setupSubscription = async () => {
      try {
        const { sseManager } = await import('../services/sseConnectionManager');

        if (cancelled) {
          return;
        }
        
        console.log(`🔴 Expanded Modal: Setting up SSE for conversation ${conversationId}`);
        setIsLiveConnected(true);
        
        const localUnsubscribe = sseManager.subscribe(conversationId, (message) => {
          try {
            if (message.type === 'message' && onLiveMessage) {
              onLiveMessage(conversationId, message.data);
            }
          } catch (error) {
            console.error('Error handling SSE message in modal:', error);
          }
        });
        unsubscribe = localUnsubscribe;

        if (cancelled) {
          localUnsubscribe();
          unsubscribe = null;
          setIsLiveConnected(false);
        }
      } catch (error) {
        console.error('Failed to setup SSE subscription in modal:', error);
        if (!cancelled) {
          setIsLiveConnected(false);
        }
      }
    };

    setupSubscription();

    return () => {
      cancelled = true;
      if (unsubscribe) {
        console.log(`🔵 Expanded Modal: Cleaning up SSE subscription for conversation: ${conversationId}`);
        unsubscribe();
        unsubscribe = null;
      }
      setIsLiveConnected(false);
    };
  }, [isOpen, conversation?.id, onLiveMessage]);

  const handleSendMessage = (text: string) => {
    if (conversation) {
      onSendMessage(conversation.id, text);
    }
  };

  const openMetadata = (data: Record<string, any>) => {
    setActiveMetadata(data);
    setIsMetadataOpen(true);
  };

  const handleOpenReactionMenu = useCallback(
    (message: Message, event: React.MouseEvent) => {
      if (!onReactMessage) return;
      event.preventDefault();
      event.stopPropagation();
      setReactionError(null);
      setReactionMenu({ messageId: message.id, x: event.clientX, y: event.clientY });
    },
    [onReactMessage]
  );

  const handleSendReaction = useCallback(
    async (reaction: ReactionKind) => {
      if (!onReactMessage || !conversation || !reactionMenu) return;
      setReactionSending(true);
      setReactionError(null);
      try {
        await onReactMessage(conversation.id, reactionMenu.messageId, reaction);
        setReactionMenu(null);
      } catch (err) {
        setReactionError(err instanceof Error ? err.message : 'Failed to send reaction.');
      } finally {
        setReactionSending(false);
      }
    },
    [conversation, onReactMessage, reactionMenu]
  );

  useEffect(() => {
    if (!reactionMenu) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setReactionMenu(null);
    };
    const handlePointerDown = (event: PointerEvent) => {
      const root = reactionMenuRef.current;
      const target = event.target as Node | null;
      if (!root || !target) return;
      if (root.contains(target)) return;
      setReactionMenu(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [reactionMenu]);

  if (!conversation) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl w-[90vw] h-[90vh] p-0 overflow-hidden !bg-white dark:!bg-gray-900 border border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              {/* Live connection indicator */}
              <div className="relative">
                <div 
                  className={`w-3 h-3 rounded-full ${isLiveConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} 
                  title={isLiveConnected ? "Live updates active" : "Connecting..."}
                />
                {isLiveConnected && (
                  <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30" />
                )}
              </div>
              
              <div className="text-center flex-1">
                <DialogTitle className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-2xl tracking-[-0.2px] m-0">
                  {conversation.name}
                </DialogTitle>
                {isLiveConnected && (
                  <span className="text-xs px-2 py-1 rounded-full font-['SF_Pro_Text',_-apple-system,_sans-serif] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 inline-block mt-1">
                    🔴 LIVE
                  </span>
                )}
              </div>
              
              <div className="text-right">
                <span className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-500 dark:text-gray-400 text-base">
                  {conversation.phoneNumber}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'messages' | 'analysis')} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 mt-4 grid w-auto grid-cols-2 bg-gray-100 dark:bg-gray-800">
            <TabsTrigger value="messages" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              💬 Messages
            </TabsTrigger>
            <TabsTrigger value="analysis" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              🧠 AI Analysis
            </TabsTrigger>
          </TabsList>

          {/* Messages Tab */}
          <TabsContent value="messages" className="flex-1 flex flex-col overflow-hidden mt-0">
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-950"
              onScroll={handleMessageScroll}
            >
              <div className="max-w-4xl mx-auto space-y-1">
                {conversation.messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onOpenMetadata={openMetadata}
                    onOpenReactionMenu={handleOpenReactionMenu}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Scroll to bottom button */}
              <AnimatePresence>
                {showScrollToBottom && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => scrollToBottom()}
                    className="fixed bottom-24 right-8 w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-50"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Scroll to latest message"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Message Input - Fixed at bottom */}
            <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
              <MessageInput onSendMessage={handleSendMessage} disabled={false} />
            </div>
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-950 mt-0">
            <div className="max-w-5xl mx-auto">
              <ConversationAnalysisPanel
                conversationId={parseInt(conversation.id)}
                conversationTitle={conversation.name}
                className="shadow-lg"
              />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
      
      {/* Metadata Dialog - Separate from main modal */}
      {isMetadataOpen && (
        <Dialog open={isMetadataOpen} onOpenChange={setIsMetadataOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-white dark:bg-gray-900 backdrop-blur-xl bg-opacity-100">
            <DialogHeader>
              <DialogTitle>Tool Call Details</DialogTitle>
              <DialogDescription>Agent metadata for this message</DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto pr-2 -mr-2">
              {activeMetadata && <MetadataViewer metadata={activeMetadata} />}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ReactionMenu
        context={reactionMenu}
        sending={reactionSending}
        error={reactionError}
        onClose={() => setReactionMenu(null)}
        onSelect={handleSendReaction}
        menuRef={reactionMenuRef}
      />
    </Dialog>
  );
}

export default ExpandedConversationModal;

